import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { Config } from '@/config';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  deleteModalRecoil,
} from '@/atom/ModalAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import Pagenation from '@/component/Pagenation';
import SearchBtn from '@/component/SearchBtn';
import DeleteModal from '@/modal/DeleteModal';
import LabelType1 from '@/component/LabelType1';
import { DeviceApi } from '@/sdk/DeviceApi';
import {
  DeviceControllerGetOriginalDataParams,
  DeleteDeviceOriginalDtoTx,
  OriginalDataDtoRx,
} from '@/sdk/data-contracts';
import { deviceTextDataListRecoil } from '@/atom/DeviceAtom';

const TextData = () => {
  const { id } = useParams<{ id: string }>();
  const deviceApi = new DeviceApi({ baseURL: Config.API_URL });
  const navigate = useNavigate();

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const [deleteModalState, setDeleteModalState] =
    useRecoilState(deleteModalRecoil);
  const [deviceTextDataListState, setDeviceTextDataListState] = useRecoilState(
    deviceTextDataListRecoil,
  );

  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<string>('desc');
  const [checkItems, setCheckItems] = useState<string[]>([]);
  const [listLength, setListLength] = useState<number>(0);
  const [theads, setTheads] = useState<string[]>([]);

  const allCheckBtnRef = useRef<HTMLInputElement>(null);

  const downloadCSV = async (saveFileName: string) => {
    let allList: OriginalDataDtoRx[] | undefined;

    const dto: DeviceControllerGetOriginalDataParams = {
      id: Number(id),
      sortBy,
      order,
    };

    try {
      const { data } = await deviceApi.deviceControllerGetOriginalData(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      if (data.originalData) allList = data.originalData;
    } catch (err: any) {}

    if (!allList || allList.length === 0) {
      setErrorModalText('다운로드할 데이터가 없습니다.');
      return;
    }

    const headers = [
      'No',
      '일시',
      '센서 상태',
      ...Object.keys(allList[0].datas),
    ];

    const rows = allList.map((list, index) => [
      index + 1,
      list.createdAt,
      list.sensorStatus,
      ...Object.values(list.datas),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.join(',')),
    ].join('\n');

    const bom = '\uFEFF';
    const csvWithBom = bom + csvContent;

    const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${saveFileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filterTextDataList = async () => {
    const dto: DeviceControllerGetOriginalDataParams = {
      id: Number(id),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      offset,
      limit,
      sortBy,
      order,
    };

    try {
      const { data } = await deviceApi.deviceControllerGetOriginalData(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });

      if (data.originalData.length === 0) {
        setTheads([]);
        setDeviceTextDataListState([]);
      } else {
        setTheads(Object.keys(data.originalData[0].datas));
        setDeviceTextDataListState(data.originalData);
      }

      if (data.totalColumn) {
        setListLength(data.totalColumn);
      } else if (data.totalColumn === 0) {
        setListLength(0);
      }
    } catch (err: any) {}
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCheckItems([]);
    setOffset(0);
    filterTextDataList();
    if (allCheckBtnRef.current) {
      allCheckBtnRef.current.checked = false;
    }
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLimit(Number(e.target.value));
    setOffset(0);
  };

  const handleOffsetChange = (value: number) => {
    setOffset((value - 1) * limit);
    setCheckItems([]);
    if (allCheckBtnRef.current) {
      allCheckBtnRef.current.checked = false;
    }
  };

  const handleSingleCheck = (checked: boolean, id: string) => {
    if (checked) {
      setCheckItems((prev) => [...prev, id]);
    } else {
      setCheckItems((prev) => prev.filter((el) => el !== id));
    }
  };

  const handleAllCheck = (checked: boolean) => {
    if (checked) {
      const idArray: string[] = [];
      deviceTextDataListState?.forEach((el) => idArray.push(el.originalDataId));
      setCheckItems(idArray);
    } else {
      setCheckItems([]);
    }
  };

  const handleSelectDeleteNumCheck = () => {
    if (checkItems.length === 0) {
      setErrorModalText('선택된 항목이 없습니다.');
      return;
    }
    setDeleteModalState(true);
  };

  const handleSelectDelete = async () => {
    try {
      const dto: DeleteDeviceOriginalDtoTx = {
        ids: checkItems,
      };
      await deviceApi.deviceControllerDeleteSensorData(Number(id), dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      setCompleteModalState(true);
    } catch (err: any) {}
  };

  useEffect(() => {
    filterTextDataList();
  }, [limit, offset]);

  return (
    <>
      <TabCntBox>
        <TableOpitonBox>
          <h1>
            전체 <span>{listLength.toLocaleString()}</span>
          </h1>
          <div className="right-box">
            <form onSubmit={handleSearch}>
              <InputArea>
                <h2>검색 기간</h2>
                <IS.TextInputType1
                  style={{ width: 145 }}
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    if (endDate !== '') {
                      if (e.target.value > endDate) {
                        setErrorModalText(
                          '시작일은 종료일보다 클 수 없습니다.',
                        );
                        return;
                      }
                    }
                    setStartDate(e.target.value);
                  }}
                />
                <h2>~</h2>
                <IS.TextInputType1
                  style={{ width: 145 }}
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    if (startDate !== '') {
                      if (e.target.value < startDate) {
                        setErrorModalText(
                          '종료일은 시작일보다 클 수 없습니다.',
                        );
                        return;
                      }
                    }
                    setEndDate(e.target.value);
                  }}
                />
                <SearchBtn />
              </InputArea>
            </form>
            <IS.SelectType1>
              <select
                value={limit}
                onChange={handleLimitChange}
                className="selected"
                style={{ width: 153 }}
              >
                <option value={10}>10개씩 보기</option>
                <option value={20}>20개씩 보기</option>
                <option value={30}>30개씩 보기</option>
                <option value={50}>50개씩 보기</option>
              </select>
              <span className="img-box">
                <img src="/img/arrow_bottom_555.svg" alt="" />
              </span>
            </IS.SelectType1>
            <BS.Btn1Type5
              type="button"
              onClick={() => downloadCSV('text_data_list')}
            >
              Excel 다운로드
              <span className="img-box">
                <img src="/img/ico_down_fff.svg" alt="" />
              </span>
            </BS.Btn1Type5>
            <BS.Btn1Type2 type="button" onClick={handleSelectDeleteNumCheck}>
              선택 삭제
            </BS.Btn1Type2>
          </div>
        </TableOpitonBox>
        <TableBox>
          <table>
            <thead>
              <tr>
                <th>
                  <IS.CheckBoxType1>
                    <input
                      type="checkbox"
                      id="all"
                      onChange={(e) => handleAllCheck(e.target.checked)}
                      ref={allCheckBtnRef}
                      checked={
                        checkItems.length === deviceTextDataListState?.length &&
                        deviceTextDataListState?.length !== 0
                          ? true
                          : false
                      }
                    />
                    <label htmlFor="all" className="img-box">
                      <img src="/img/ico_check_1.svg" alt="" />
                    </label>
                  </IS.CheckBoxType1>
                </th>
                <th>No</th>
                <th>일시</th>
                <th>센서 상태</th>
                {theads.map((thead, index) => (
                  <th key={index}>{thead}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {deviceTextDataListState?.map((list, index) => (
                <tr key={index}>
                  <td>
                    <IS.CheckBoxType1>
                      <input
                        type="checkbox"
                        id={`cv-${index}`}
                        onChange={(e) =>
                          handleSingleCheck(
                            e.target.checked,
                            list.originalDataId,
                          )
                        }
                        checked={
                          checkItems.includes(list.originalDataId)
                            ? true
                            : false
                        }
                      />
                      <label htmlFor={`cv-${index}`} className="img-box">
                        <img src="/img/ico_check_1.svg" alt="" />
                      </label>
                    </IS.CheckBoxType1>
                  </td>
                  <td>{(offset + index + 1).toLocaleString()}</td>
                  <td>{list.createdAt}</td>
                  <td>
                    {list.sensorStatus !== '' && (
                      <LabelType1 labelValue={list.sensorStatus} />
                    )}
                  </td>
                  {Object.entries(list.datas).map(([key, value]) => (
                    <td key={key}>{value}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </TableBox>
        {listLength !== 0 && (
          <Pagenation
            listAllLength={listLength}
            listLimit={limit}
            onCurrentPageChange={handleOffsetChange}
          />
        )}
      </TabCntBox>
      {deleteModalState && <DeleteModal onConfirm={handleSelectDelete} />}
    </>
  );
};

export default TextData;

export const CntBox1 = styled(CS.CntBoxType1)`
  width: 100%;
`;

export const CntBox2 = styled(CS.CntBoxType1)`
  width: 100%;
  margin-top: 20px;
`;

export const InforWrap = styled.div`
  width: 100%;
  display: flex;
  gap: 20px;
  margin-top: 16px;
`;

export const InforBox1 = styled(CS.CntBoxType2)`
  position: relative;
  width: calc(50% - 13.3333px);
  > .device-btn {
    position: absolute;
    right: 20px;
    bottom: 20px;
  }
`;

export const InforBox2 = styled(CS.CntBoxType2)`
  width: calc(25% - 13.3333px);
  > .text-wrap {
    &:before {
      display: none;
    }
    > .text-box {
      width: 100%;
      &:first-child {
        padding-right: 0;
      }
      &:last-child {
        padding-left: 0;
      }
    }
  }
`;

export const InforBox3 = styled(CS.CntBoxType2)`
  width: calc(25% - 13.3333px);
  > .img-area {
    width: 100%;
    position: relative;
    margin-top: 12px;
    > button {
      position: absolute;
      width: 34px;
      height: 34px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      border: 1px solid #ececec;
      background: #fff;
      border-radius: 4px;
      left: 10px;
      bottom: 10px;
      > .img-box {
        width: 24px;
      }
    }
  }
`;

export const SearchOptionBox = styled(CS.SearchOptionBox)`
  width: 100%;
  margin-top: 20px;
`;

export const InputWrap = styled.div`
  gap: 8px;
  display: flex;
  flex-direction: column;
`;

export const InputArea = styled.div`
  gap: 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  > h2 {
    color: #555;
    font-size: 14px;
  }
`;

export const TabCntBox = styled.div`
  width: 100%;
  margin-top: 20px;
`;

export const TableOpitonBox = styled(CS.TableOpitonBox)`
  width: 100%;
  margin-top: 0;
`;

export const TableOpitonBox2 = styled(TableOpitonBox)`
  margin-top: 32px;
`;

export const TableBox = styled(CS.TableType1)`
  width: 100%;
  margin-top: 16px;
  margin-bottom: 32px;
`;

export const MapBox = styled.div`
  width: 100%;
  height: 400px;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
`;

export const MapModal = styled.div`
  position: absolute;
  z-index: 10;
  top: 50%;
  left: 60%;
  transform: translate(-50%, -50%);
  width: 136px;
  border-radius: 4px;
  box-sizing: border-box;
  border: 1px solid #d9d9d9;
  box-shadow: 4px 4px 12px 0px rgba(255, 255, 255, 0.25);
  overflow: hidden;
  > .title-box {
    box-sizing: border-box;
    padding: 8px 12px;
    background: #e9e8e8;
    font-size: 16px;
    font-weight: 500;
  }
  > .text-box {
    box-sizing: border-box;
    padding: 12px;
    background: #fff;
    > .state-box {
      display: flex;
      align-items: center;
      gap: 8px;
      > .text {
        font-size: 15px;
      }
      > .line {
        width: 1px;
        height: 10px;
        background: #d9d9d9;
      }
    }
    > .date {
      margin-top: 6px;
      font-size: 12px;
      font-weight: 300;
      color: #777;
    }
    > button {
      margin-top: 8px;
      font-size: 14px;
      color: #3c8deb;
    }
  }
`;

export const MapTextModal = styled.div`
  position: absolute;
  z-index: 10;
  top: 50%;
  left: 70%;
  transform: translate(-50%, -50%);
  border-radius: 4px;
  box-sizing: border-box;
  border: 1px solid #d9d9d9;
  padding: 10px 16px;
  box-shadow: 4px 4px 12px 0px rgba(255, 255, 255, 0.25);
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  > p {
    font-size: 15px;
    white-space: nowrap;
  }
  > button {
    width: 10px;
    min-width: 10px;
  }
`;

export const GraphBox = styled.div`
  margin-top: 16px;
  width: 100%;
  height: 280px;
  box-sizing: border-box;
  border-top: 2px solid #222222;
`;
